import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader, StatusChip, ConfidenceBadge } from "@/components/Bits";
import SourceDocViewer from "@/components/SourceDocViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ComboSelect from "@/components/ComboSelect";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function SalesDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [si, setSi] = useState(null);
    const [form, setForm] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [transporters, setTransporters] = useState([]);
    const [salesLedgers, setSalesLedgers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [departments, setDepartments] = useState([]);

    const load = async () => {
        const [s, cust, trans, allLedg, locs, deps] = await Promise.all([
            api.get(`/sales-invoices/${id}`),
            api.get("/ledgers", { params: { party_type: "customer" } }),
            api.get("/ledgers", { params: { party_type: "transporter" } }),
            api.get("/ledgers"),
            api.get("/masters/locations"),
            api.get("/masters/departments"),
        ]);
        setSi(s.data);
        setCustomers(cust.data);
        setTransporters(trans.data);
        // "Type of Sales" picker — only real revenue lines. Excludes commission
        // (an expense-side income offset booked separately) and Fiat (parallel
        // books) so the founder doesn't accidentally book product revenue there.
        setSalesLedgers(allLedg.data.filter((l) => {
            const g = (l.group_name || "").toLowerCase();
            const n = (l.name || "").toLowerCase();
            if (!g.startsWith("sales")) return false;
            if (g.includes("commission") || n.includes("commission")) return false;
            if (g.includes("fiat") || n.includes("fiat")) return false;
            return true;
        }));
        setLocations(locs.data); setDepartments(deps.data);
        const p = s.data;
        setForm({
            customer_ledger_id: p.customer_ledger_id || "",
            customer_name: p.customer_name || "",
            customer_gstin: p.customer_gstin || "",
            sales_ledger_id: p.sales_ledger_id || "",
            invoice_no: p.invoice_no || "",
            invoice_date: p.invoice_date || "",
            subtotal: p.subtotal || 0,
            gst_amount: p.gst_amount || 0,
            cgst_amount: p.cgst_amount ?? p.extracted?.cgst ?? 0,
            sgst_amount: p.sgst_amount ?? p.extracted?.sgst ?? 0,
            igst_amount: p.igst_amount ?? p.extracted?.igst ?? 0,
            total: p.total || 0,
            lines: p.lines || [],
            outward_transport_amount: p.outward_transport_amount ?? p.outward_transport?.amount ?? null,
            outward_transport_transporter_ledger_id: p.outward_transport_transporter_ledger_id ?? p.outward_transport?.transporter_ledger_id ?? "",
            outward_transport_transporter_name: p.outward_transport_transporter_name ?? p.outward_transport?.transporter_name ?? "",
            dept: p.dept || "",
            location: p.location || "",
        });
    };
    useEffect(() => { load(); }, [id]);

    // Poll while AI OCR is running.
    useEffect(() => {
        if (si?.status !== "extracting") return;
        const t = setInterval(() => { load(); }, 2500);
        return () => clearInterval(t);
    }, [si?.status]);

    if (!si || !form) return <div className="p-8 text-stone-500">Loading…</div>;

    if (si.status === "extracting") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-50 p-8" data-testid="si-extracting">
                <div className="bg-white border rounded-md p-8 max-w-md w-full text-center space-y-4"
                     style={{ borderColor: "var(--se-border)" }}>
                    <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                         style={{ background: "var(--se-brand-soft)" }}>
                        <svg className="animate-spin" width="32" height="32" viewBox="0 0 24 24" fill="none"
                             stroke="var(--se-brand)" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                    </div>
                    <h2 className="font-serif text-2xl">Reading your invoice…</h2>
                    <p className="text-sm text-stone-500">
                        AI is extracting customer, GSTIN, line items, HSN, and totals.
                        Usually takes <b>20-60 seconds</b>.
                    </p>
                    <p className="text-xs text-stone-400">
                        You can navigate away — it&apos;ll show up in the Sales list when ready.
                    </p>
                </div>
            </div>
        );
    }

    if (si.status === "extraction_failed") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-50 p-8" data-testid="si-extraction-failed">
                <div className="bg-white border rounded-md p-8 max-w-md w-full text-center space-y-4"
                     style={{ borderColor: "#fecaca", background: "#fff1f2" }}>
                    <h2 className="font-serif text-2xl" style={{ color: "#9f1239" }}>Extraction failed</h2>
                    <p className="text-sm text-stone-600">
                        AI couldn&apos;t read this file:
                        <br /><code className="text-xs">{si.extraction_error || "unknown error"}</code>
                    </p>
                    <Button variant="outline" onClick={() => nav("/sales")} data-testid="si-back-list">
                        Back to list
                    </Button>
                </div>
            </div>
        );
    }
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setLine = (i, k, v) => setForm((f) => {
        const lines = f.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l);
        if (k === "qty" || k === "rate") {
            const line = lines[i];
            const qty = Number(line.qty) || 0;
            const rate = Number(line.rate) || 0;
            if (qty > 0 && rate > 0) {
                const taxable = +(qty * rate).toFixed(2);
                line.taxable = taxable;
                const gstRate = Number(line.gst_rate) || 0;
                const gstAmt = +(taxable * gstRate / 100).toFixed(2);
                line.gst_amount = gstAmt;
                line.total = +(taxable + gstAmt).toFixed(2);
            }
        }
        if (k === "gst_rate") {
            const line = lines[i];
            const taxable = Number(line.taxable) || 0;
            const gstRate = Number(line.gst_rate) || 0;
            const gstAmt = +(taxable * gstRate / 100).toFixed(2);
            line.gst_amount = gstAmt;
            line.total = +(taxable + gstAmt).toFixed(2);
        }
        return { ...f, lines };
    });
    const addLine = () => setForm((f) => ({ ...f, lines: [...(f.lines || []), { description: "", hsn: "", qty: null, uom: "", rate: null, taxable: 0, gst_rate: 18, gst_amount: 0, total: 0 }]}));
    const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

    const save = async () => {
        try {
            if (!form.sales_ledger_id) return toast.error("Choose the type of sales");
            await api.patch(`/sales-invoices/${id}`, { ...form, subtotal: Number(form.subtotal), gst_amount: Number(form.gst_amount), cgst_amount: Number(form.cgst_amount||0), sgst_amount: Number(form.sgst_amount||0), igst_amount: Number(form.igst_amount||0), total: Number(form.total), outward_transport_amount: form.outward_transport_amount ? Number(form.outward_transport_amount) : null });
            toast.success("Draft saved"); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const canPost = user?.role === "founder" || user?.role === "accountant";
    const confirm = async () => {
        if (!canPost) return toast.error("Only Founder / Accountant can post to ledgers. Save your changes — a reviewer will post it.");
        try {
            if (!form.sales_ledger_id) return toast.error("Choose the type of sales");
            await api.post(`/sales-invoices/${id}/confirm`, { ...form, subtotal: Number(form.subtotal), gst_amount: Number(form.gst_amount), cgst_amount: Number(form.cgst_amount||0), sgst_amount: Number(form.sgst_amount||0), igst_amount: Number(form.igst_amount||0), total: Number(form.total), outward_transport_amount: form.outward_transport_amount ? Number(form.outward_transport_amount) : null });
            toast.success("Posted"); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const discard = async () => {
        if (!window.confirm("Discard this draft? The uploaded document will be marked deleted.")) return;
        try {
            await api.delete(`/sales-invoices/${id}`);
            toast.success("Draft discarded");
            nav("/sales");
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const reverseAndEdit = async () => {
        const reason = window.prompt("Reason for editing? (e.g. wrong customer / amounts)");
        if (!reason) return;
        try {
            await api.post(`/sales-invoices/${id}/reverse`, null, { params: { reason, to_draft: true }});
            toast.success("Reversed — you can edit now and re-post");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const reverseOnly = async () => {
        const reason = window.prompt("Reason for reversal?");
        if (!reason) return;
        try {
            await api.post(`/sales-invoices/${id}/reverse`, null, { params: { reason }});
            toast.success("Reversed");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const cancelInvoice = async () => {
        const reason = window.prompt("Why is this invoice being cancelled? (e.g. customer cancelled the order)");
        if (!reason) return;
        const noun = si.doc_type === "credit_note" ? "credit note" : "invoice";
        if (!window.confirm(`Cancel this ${noun}? Its journal will be reversed and any linked credit notes will also be cancelled. This is different from Reverse & Edit — a cancelled ${noun} cannot be re-posted.`)) return;
        try {
            await api.post(`/sales-invoices/${id}/cancel`, null, { params: { reason }});
            toast.success(`${noun[0].toUpperCase()}${noun.slice(1)} cancelled`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const isPosted = si.status === "posted";
    const dupInfo = si.duplicate_info;

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Sales invoice" sub={<>{si.customer_name || "—"} · Inv {si.invoice_no || "—"}</>}
                right={<>
                    <StatusChip status={si.status} />
                    {si.confidence != null && !isPosted ? <ConfidenceBadge value={si.confidence} /> : null}
                    {si.duplicate_of && !isPosted ? <span className="se-chip se-chip-err">Possible duplicate</span> : null}
                    {isPosted ? (
                        <a href={`${api.defaults.baseURL}/sales-invoices/${si.id}/pdf`} target="_blank" rel="noopener noreferrer"
                           className="text-xs uppercase tracking-widest hover:underline"
                           style={{ color: "var(--se-brand)" }}
                           data-testid="si-view-pdf">
                            GST invoice PDF ↗
                        </a>
                    ) : null}
                    {isPosted && form.customer_ledger_id ? (
                        <Link to={`/ledgers/${form.customer_ledger_id}`} className="text-xs uppercase tracking-widest hover:underline" style={{ color: "var(--se-brand)" }} data-testid="si-customer-ledger-link">
                            View customer ledger →
                        </Link>
                    ) : null}
                </>} />
            {dupInfo && !isPosted ? (
                <div className="mx-8 mt-6 border-l-4 rounded-sm p-4 flex items-start gap-3 flex-wrap" style={{ borderColor: "#991b1b", background: "var(--se-err-bg)", color: "var(--se-err-text)" }} data-testid="dup-banner">
                    <div className="flex-1 min-w-[260px]">
                        <div className="font-medium">This sales invoice looks already uploaded.</div>
                        <div className="text-xs mt-1">
                            <b>{dupInfo.customer_name}</b> · Inv <b>{dupInfo.invoice_no}</b> · ₹{Number(dupInfo.total).toLocaleString("en-IN")}
                            {" · "}<span className="uppercase tracking-widest">{dupInfo.status}</span>
                            {dupInfo.invoice_date ? <> · dated {dupInfo.invoice_date}</> : null}
                        </div>
                        <div className="text-xs mt-1">Please review the existing one and discard this draft if it&apos;s the same bill.</div>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => nav(`/sales/${dupInfo.id}`)} data-testid="dup-open-existing">Open existing</Button>
                        <Button size="sm" onClick={discard} style={{ background: "#991b1b", color: "#fff" }} data-testid="dup-discard">Discard this draft</Button>
                    </div>
                </div>
            ) : null}
            <div className="p-8 grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
                <div className="bg-white border rounded-md xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-auto"
                     style={{ borderColor: "var(--se-border)" }}
                     data-testid="si-source-doc-sticky">
                    <div className="px-5 py-3 border-b text-xs uppercase tracking-widest text-stone-500 sticky top-0 bg-white z-10" style={{ borderColor: "var(--se-border)" }}>Source document</div>
                    <div className="p-5">
                        <SourceDocViewer documentId={si.document_id} filename={si.document_filename || `${si.customer_name || "invoice"}_${si.invoice_no || si.id}.pdf`} />
                    </div>
                </div>
                <div className="space-y-6">
                    <div className="bg-white border rounded-md p-5 grid grid-cols-2 gap-4" style={{ borderColor: "var(--se-border)" }}>
                        <div className="col-span-2">
                            <Label>Customer (existing)</Label>
                            <ComboSelect
                                value={form.customer_ledger_id || ""}
                                onValueChange={(v) => set("customer_ledger_id", v === "__new__" ? "" : v)}
                                options={[
                                    { id: "__new__", name: "+ Add new customer" },
                                    ...customers.map((c) => ({ id: c.id, name: c.name, hint: c.gstin || undefined }))
                                ]}
                                placeholder="Select or add"
                                testId="si-customer"
                            />
                        </div>
                        <div><Label>Customer name (new)</Label><Input value={form.customer_name || ""} disabled={!!form.customer_ledger_id} onChange={(e) => set("customer_name", e.target.value)} data-testid="si-cust-name" /></div>
                        <div><Label>Customer GSTIN (blank for B2C)</Label><Input value={form.customer_gstin || ""} disabled={!!form.customer_ledger_id} onChange={(e) => set("customer_gstin", e.target.value)} data-testid="si-cust-gstin" /></div>
                        <div>
                            <Label>Type of Sales</Label>
                            <Select value={form.sales_ledger_id || ""} onValueChange={(v) => set("sales_ledger_id", v)}>
                                <SelectTrigger data-testid="si-sales-ledger"><SelectValue placeholder="Choose" /></SelectTrigger>
                                <SelectContent>{salesLedgers.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label>Invoice no.</Label><Input value={form.invoice_no || ""} onChange={(e) => set("invoice_no", e.target.value)} data-testid="si-invno" /></div>
                        <div><Label>Invoice date</Label><Input type="date" value={form.invoice_date || ""} onChange={(e) => set("invoice_date", e.target.value)} data-testid="si-invdate" /></div>
                        <div><Label>Department <span className="text-stone-400 font-normal">(optional)</span></Label>
                            <Select value={form.dept || ""} onValueChange={(v) => set("dept", v)}><SelectTrigger data-testid="si-dept"><SelectValue placeholder="Choose (or leave blank)" /></SelectTrigger><SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent></Select>
                            <p className="text-[11px] text-stone-500 mt-1">Only tag when the sale belongs to a specific team&apos;s budget (rarely needed for regular product sales).</p>
                        </div>
                        <div><Label>Location <span className="text-stone-400 font-normal">(optional)</span></Label>
                            <Select value={form.location || ""} onValueChange={(v) => set("location", v)}><SelectTrigger data-testid="si-location"><SelectValue placeholder="Choose (or leave blank)" /></SelectTrigger><SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent></Select>
                        </div>
                        <div><Label>Subtotal</Label><Input type="number" value={form.subtotal} onChange={(e) => set("subtotal", e.target.value)} data-testid="si-subtotal" /></div>
                        <div><Label>GST (total)</Label><Input type="number" value={form.gst_amount} onChange={(e) => set("gst_amount", e.target.value)} data-testid="si-gst" /></div>
                        <div><Label>Total</Label><Input type="number" value={form.total} onChange={(e) => set("total", e.target.value)} data-testid="si-total" /></div>
                        <div className="col-span-2 grid grid-cols-3 gap-3">
                            <div><Label className="text-[11px] uppercase tracking-widest text-stone-500">CGST</Label><Input type="number" value={form.cgst_amount ?? 0} onChange={(e) => set("cgst_amount", e.target.value)} data-testid="si-cgst" /></div>
                            <div><Label className="text-[11px] uppercase tracking-widest text-stone-500">SGST</Label><Input type="number" value={form.sgst_amount ?? 0} onChange={(e) => set("sgst_amount", e.target.value)} data-testid="si-sgst" /></div>
                            <div><Label className="text-[11px] uppercase tracking-widest text-stone-500">IGST</Label><Input type="number" value={form.igst_amount ?? 0} onChange={(e) => set("igst_amount", e.target.value)} data-testid="si-igst" /></div>
                            <p className="col-span-3 text-[11px] text-stone-500">Split so CGST+SGST (intra-state) or IGST (inter-state) posts to the right ledger for set-off. Sum should match the GST total above.</p>
                        </div>
                    </div>

                    <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}>
                        <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--se-brand)" }}>Line items (product-wise)</div>
                            <Button variant="ghost" size="sm" onClick={addLine} data-testid="si-add-line">+ Add line</Button>
                        </div>
                        <div className="p-3 overflow-x-auto">
                            <table className="se-table" style={{ minWidth: 900 }}>
                                <thead><tr><th>Item name</th><th>HSN</th><th className="text-right">Qty</th><th>UoM</th><th className="text-right">Rate</th><th className="text-right">Taxable</th><th className="text-right">GST %</th><th className="text-right">GST ₹</th><th className="text-right">Total</th><th></th></tr></thead>
                                <tbody>
                                    {(form.lines || []).map((l, i) => (
                                        <tr key={i}>
                                            <td><Input value={l.description || ""} onChange={(e) => setLine(i, "description", e.target.value)} className="h-8 text-xs min-w-[160px]" data-testid={`si-line-desc-${i}`} /></td>
                                            <td><Input value={l.hsn || ""} onChange={(e) => setLine(i, "hsn", e.target.value)} className="h-8 text-xs w-20" /></td>
                                            <td><Input value={l.qty ?? ""} onChange={(e) => setLine(i, "qty", e.target.value === "" ? null : Number(e.target.value))} className="h-8 text-xs w-20 text-right" data-testid={`si-line-qty-${i}`} /></td>
                                            <td><Input value={l.uom || ""} onChange={(e) => setLine(i, "uom", e.target.value)} placeholder="Nos" className="h-8 text-xs w-16" data-testid={`si-line-uom-${i}`} /></td>
                                            <td><Input value={l.rate ?? ""} onChange={(e) => setLine(i, "rate", e.target.value === "" ? null : Number(e.target.value))} className="h-8 text-xs w-24 text-right" data-testid={`si-line-rate-${i}`} /></td>
                                            <td><Input value={l.taxable ?? ""} onChange={(e) => setLine(i, "taxable", Number(e.target.value))} className="h-8 text-xs w-24 text-right" /></td>
                                            <td><Input value={l.gst_rate ?? ""} onChange={(e) => setLine(i, "gst_rate", Number(e.target.value))} className="h-8 text-xs w-14 text-right" /></td>
                                            <td><Input value={l.gst_amount ?? ""} onChange={(e) => setLine(i, "gst_amount", Number(e.target.value))} className="h-8 text-xs w-20 text-right" /></td>
                                            <td><Input value={l.total ?? ""} onChange={(e) => setLine(i, "total", Number(e.target.value))} className="h-8 text-xs w-24 text-right" /></td>
                                            <td><button className="text-xs text-stone-400 hover:text-red-700" onClick={() => removeLine(i)}>×</button></td>
                                        </tr>
                                    ))}
                                    {(form.lines || []).length === 0 ? <tr><td colSpan="10" className="text-stone-400 py-4 text-center">No lines extracted — add manually or re-upload.</td></tr> : null}
                                </tbody>
                            </table>
                            <p className="text-xs text-stone-500 mt-3">Product-wise Qty × Rate feeds the sales analytics dashboard later. UoM (Nos, Kg, Sq.ft…) is stored per line.</p>
                        </div>
                    </div>

                    <div className="bg-white border rounded-md p-5 grid grid-cols-2 gap-4" style={{ borderColor: "var(--se-border)" }}>
                        <div className="col-span-2 text-xs uppercase tracking-widest text-stone-500">Outward transport (optional)</div>
                        <div><Label>Transporter</Label>
                            <ComboSelect
                                value={form.outward_transport_transporter_ledger_id || ""}
                                onValueChange={(v) => set("outward_transport_transporter_ledger_id", v === "__new__" ? "" : v)}
                                options={[
                                    { id: "__new__", name: "+ Add new" },
                                    ...transporters.map((t) => ({ id: t.id, name: t.name }))
                                ]}
                                placeholder="Choose or add"
                                testId="si-transporter"
                            />
                        </div>
                        <div><Label>Or new transporter name</Label><Input value={form.outward_transport_transporter_name || ""} disabled={!!form.outward_transport_transporter_ledger_id} onChange={(e) => set("outward_transport_transporter_name", e.target.value)} data-testid="si-transporter-new" /></div>
                        <div><Label>Freight amount</Label><Input type="number" value={form.outward_transport_amount ?? ""} onChange={(e) => set("outward_transport_amount", e.target.value)} data-testid="si-freight" /></div>
                        <div className="text-[11px] text-stone-500 flex items-end pb-2">RCM auto-applied when the selected transporter has GTA / RCM set to <b className="mx-1">Yes</b> in Settings.</div>
                    </div>

                    <div className="flex gap-3 flex-wrap">
                        {!isPosted ? (
                            <>
                                {!canPost ? (
                                    <div className="text-[11px] px-3 py-2 rounded-sm bg-amber-50 border border-amber-300 text-amber-900" data-testid="si-review-hint">
                                        You can edit and save the draft. A Founder or Accountant will review and post it.
                                    </div>
                                ) : null}
                                <Button variant="outline" onClick={save} data-testid="si-save">Save draft</Button>
                                {canPost ? (
                                    <Button onClick={confirm} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="si-confirm">Review & Post</Button>
                                ) : null}
                                <Button variant="ghost" onClick={discard} style={{ color: "#991b1b" }} data-testid="si-discard">Discard draft</Button>
                            </>
                        ) : (
                            <>
                                <div className="text-stone-500 text-sm mr-3">Posted on {si.posted_at?.slice(0, 10)}</div>
                                {canPost ? (
                                    <>
                                        <Button onClick={reverseAndEdit} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="si-reverse-edit">Reverse & Edit</Button>
                                        <Button variant="outline" onClick={reverseOnly} data-testid="si-reverse">Reverse posted entry</Button>
                                        <Button variant="outline" onClick={cancelInvoice} style={{ borderColor: "#e11d48", color: "#9f1239" }} data-testid="si-cancel">Cancel</Button>
                                    </>
                                ) : null}
                            </>
                        )}
                        <Button variant="ghost" onClick={() => nav("/sales")}>Back</Button>
                    </div>
                </div>
            </div>
        </>
    );
}
